import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Plus, Trash2, Crown, ArrowDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type OwnerType = "agency" | "client" | "prospect";

interface CompanyAdminsCardProps {
  ownerType: OwnerType;
  ownerId: string | number;
  readOnly?: boolean;
}

interface AdminUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isAdmin: boolean;
  isPrimaryAdmin?: boolean;
}

export default function CompanyAdminsCard({ ownerType, ownerId, readOnly }: CompanyAdminsCardProps) {
  if (ownerType === "prospect") return null;

  const qc = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedUserId, setSelectedUserId] = React.useState<string>("");
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState<"promote" | "demote" | null>(null);
  const [confirmTarget, setConfirmTarget] = React.useState<AdminUser | null>(null);

  const queryKey = ["/api/org/admins", ownerType, ownerId];

  const { data: admins = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const r = await fetch(`/api/org/admins?ownerType=${ownerType}&ownerId=${ownerId}`, {
        credentials: "include",
      });
      return r.ok ? r.json() : [];
    },
  });

  const viewerIsPrimaryAdmin = React.useMemo(() => {
    if (!user?.id) return false;
    const viewerIds = new Set<string>([String(user.id)]);
    if ((user as any)?.employeeId) viewerIds.add(`emp_${(user as any).employeeId}`);
    return (admins as AdminUser[]).some(
      (a) => a.isPrimaryAdmin && viewerIds.has(a.id)
    );
  }, [admins, ownerType, user]);

  // Agency-side viewers (brokers/agency_members/superadmins) manage their
  // clients' admins on the client's behalf, so they don't need to be a Primary
  // Admin of the client. Self-service PA gating still applies to agency orgs.
  const viewerIsAgencyManager = React.useMemo(() => {
    const role = (user as any)?.role as string | undefined;
    const agencySide = role === "broker" || role === "agency_member" || role === "superadmin";
    return agencySide && ownerType !== "agency";
  }, [user, ownerType]);

  const viewerCanManage = viewerIsPrimaryAdmin || viewerIsAgencyManager;

  const viewerAdminIds = React.useMemo(() => {
    const ids = new Set<string>();
    if (user?.id) ids.add(String(user.id));
    if ((user as any)?.employeeId) ids.add(`emp_${(user as any).employeeId}`);
    return ids;
  }, [user]);

  const primaryAdminCount = React.useMemo(() => {
    return (admins as AdminUser[]).filter((a) => a.isPrimaryAdmin).length;
  }, [admins]);

  const { data: availableUsers = [] } = useQuery({
    queryKey: ["/api/org/available-admins", ownerType, ownerId],
    queryFn: async () => {
      const r = await fetch(`/api/org/available-admins?ownerType=${ownerType}&ownerId=${ownerId}`, {
        credentials: "include",
      });
      return r.ok ? r.json() : [];
    },
    enabled: dialogOpen,
  });

  const addAdminMutation = useMutation({
    mutationFn: async (userId: string) => {
      const r = await fetch("/api/org/admins", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ownerType, ownerId, userId }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || "Failed to add admin");
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["/api/org/available-admins", ownerType, ownerId] });
      toast({ title: "Admin added successfully", variant: "success" });
      setDialogOpen(false);
      setSelectedUserId("");
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const removeAdminMutation = useMutation({
    mutationFn: async (userId: string) => {
      const r = await fetch(`/api/org/admins/${userId}?ownerType=${ownerType}&ownerId=${ownerId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || "Failed to remove admin");
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["/api/org/available-admins", ownerType, ownerId] });
      toast({ title: "Admin removed successfully", variant: "success" });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const makePrimaryAdminMutation = useMutation({
    mutationFn: async (userId: string) => {
      const r = await fetch(`/api/org/admins/${userId}/primary-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ownerType, ownerId }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || "Failed to promote to Primary Admin");
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast({ title: "User promoted to Primary Admin", variant: "success" });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const demotePrimaryAdminMutation = useMutation({
    mutationFn: async (userId: string) => {
      const r = await fetch(`/api/org/admins/${userId}/demote-primary-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ownerType, ownerId }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || "Failed to demote Primary Admin");
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast({ title: "Primary Admin demoted", variant: "success" });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const handleAddAdmin = () => {
    if (selectedUserId) {
      addAdminMutation.mutate(selectedUserId);
    }
  };

  const getPortalLabel = () => {
    switch (ownerType) {
      case "agency": return "agency portal";
      case "client": return "employer portal";
      case "prospect": return "employer portal";
      default: return "portal";
    }
  };

  const openConfirm = (action: "promote" | "demote", admin: AdminUser) => {
    setConfirmAction(action);
    setConfirmTarget(admin);
    setConfirmOpen(true);
  };

  const runConfirmedAction = () => {
    if (!confirmAction || !confirmTarget) return;
    if (confirmAction === "promote") {
      makePrimaryAdminMutation.mutate(confirmTarget.id);
    } else {
      demotePrimaryAdminMutation.mutate(confirmTarget.id);
    }
    setConfirmOpen(false);
    setConfirmAction(null);
    setConfirmTarget(null);
  };

  const confirmTitle = React.useMemo(() => {
    if (!confirmAction) return "Are you sure?";
    return confirmAction === "promote" ? "Promote to Primary Admin?" : "Demote Primary Admin?";
  }, [confirmAction]);

  const confirmDescription = React.useMemo(() => {
    if (!confirmAction || !confirmTarget) return "";
    const name = confirmTarget.firstName && confirmTarget.lastName
      ? `${confirmTarget.firstName} ${confirmTarget.lastName}`
      : confirmTarget.email;

    if (confirmAction === "promote") {
      return `${name} will gain Primary Admin permissions and can manage company admins.`;
    }

    return `${name} will lose Primary Admin permissions and remain a regular admin.`;
  }, [confirmAction, confirmTarget]);

  return (
    <>
      <Card className="flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <ShieldCheck className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base">Company Admins</CardTitle>
                <CardDescription className="text-xs">
                  Users with full administrative rights to this {getPortalLabel()}
                </CardDescription>
              </div>
            </div>
            {viewerCanManage && !readOnly ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialogOpen(true)}
                className="h-8"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            ) : null}
          </div>
          {!viewerCanManage && (
            <div className="text-xs text-muted-foreground pt-2">
              Only the Primary Admin can manage company admins.
            </div>
          )}
        </CardHeader>
        <CardContent className="flex-1 pt-0">
          {isLoading ? (
            <div className="text-center py-6 text-muted-foreground">
              <p className="text-sm">Loading...</p>
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <ShieldCheck className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No company admins yet</p>
              {viewerCanManage && !readOnly ? (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setDialogOpen(true)}
                  className="mt-1"
                >
                  Add your first admin
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="space-y-2">
              {admins.map((admin: AdminUser) => (
                <div
                  key={admin.id}
                  className={cn(
                    "flex items-center justify-between p-2.5 rounded-lg border transition-colors group",
                    admin.isPrimaryAdmin 
                      ? "bg-amber-50 border-amber-200 hover:bg-amber-100" 
                      : "bg-muted/30 hover:bg-muted/50"
                  )}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Crown className={cn(
                      "h-4 w-4 flex-shrink-0",
                      admin.isPrimaryAdmin ? "text-amber-600" : "text-amber-500"
                    )} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {admin.firstName && admin.lastName 
                            ? `${admin.firstName} ${admin.lastName}`
                            : admin.email}
                        </span>
                        {admin.isPrimaryAdmin && (
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-1.5 py-0">
                            Primary Admin
                          </Badge>
                        )}
                      </div>
                      {admin.firstName && admin.lastName && (
                        <span className="text-xs text-muted-foreground truncate block">
                          {admin.email}
                        </span>
                      )}
                    </div>
                  </div>
                  {viewerCanManage && !readOnly ? (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {admin.isPrimaryAdmin ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          onClick={() => openConfirm("demote", admin)}
                          disabled={
                            demotePrimaryAdminMutation.isPending ||
                            primaryAdminCount <= 1 ||
                            viewerAdminIds.has(admin.id)
                          }
                          title={primaryAdminCount <= 1 ? "Cannot demote the last Primary Admin" : "Demote Primary Admin"}
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          onClick={() => openConfirm("promote", admin)}
                          disabled={makePrimaryAdminMutation.isPending}
                          title="Promote to Primary Admin"
                        >
                          <Crown className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!admin.isPrimaryAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => removeAdminMutation.mutate(admin.id)}
                          disabled={removeAdminMutation.isPending}
                          title="Remove Admin"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Company Admin</DialogTitle>
            <DialogDescription>
              Select a user to grant full administrative rights to this {getPortalLabel()}.
              Admins can manage all aspects of the organization.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user">Select User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <SelectItem value="no-users" disabled>
                      No available users
                    </SelectItem>
                  ) : (
                    availableUsers.map((user: AdminUser) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName} (${user.email})`
                          : user.email}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddAdmin}
              disabled={!selectedUserId || addAdminMutation.isPending}
            >
              {addAdminMutation.isPending ? "Adding..." : "Add Admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setConfirmOpen(false);
                setConfirmAction(null);
                setConfirmTarget(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runConfirmedAction}
              disabled={
                makePrimaryAdminMutation.isPending ||
                demotePrimaryAdminMutation.isPending ||
                !confirmAction ||
                !confirmTarget
              }
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
